import type { Data } from "@puckeditor/core";
import { stylablePuckConfig } from "@/routes/pages/editor-puck-config";

const BLOG_COMPONENT_NAMES = [
  "Heading",
  "Text",
  "RichText",
  "Image",
  "ImageEnhanced",
  "Button",
  "Divider",
  "Blockquote",
  "CodeBlock",
  "MapEmbed",
  "Hero",
] as const;

type BlogComponentName = typeof BLOG_COMPONENT_NAMES[number];

const BLOG_COMPONENT_SET = new Set<string>(BLOG_COMPONENT_NAMES);

function createParagraphBlock(content: string, index: number) {
  return {
    type: "RichText",
    props: {
      id: `blog-paragraph-${index + 1}`,
      content,
      maxWidth: "720px",
      fontSize: "md",
    },
  };
}

function normalizeContentArray(content: unknown[]): {
  content: Array<{ type: string; props: Record<string, unknown> }>;
  unsupportedTypes: string[];
} {
  const unsupportedTypes: string[] = [];
  const normalized = content.flatMap((item, index) => {
    if (typeof item === "string") {
      return [createParagraphBlock(item, index)];
    }

    if (!item || typeof item !== "object") {
      return [];
    }

    const candidate = item as { type?: unknown; props?: unknown };
    const type = typeof candidate.type === "string" ? candidate.type : null;
    if (!type) {
      return [];
    }

    if (!BLOG_COMPONENT_SET.has(type)) {
      unsupportedTypes.push(type);
      return [];
    }

    return [{
      type,
      props: candidate.props && typeof candidate.props === "object"
        ? { ...(candidate.props as Record<string, unknown>) }
        : {},
    }];
  });

  return { content: normalized, unsupportedTypes };
}

export function normalizeBlogPuckData(input: unknown): {
  data: Data;
  unsupportedTypes: string[];
} {
  if (typeof input === "string") {
    return {
      data: { root: {}, content: [createParagraphBlock(input, 0)] },
      unsupportedTypes: [],
    };
  }

  if (Array.isArray(input)) {
    const normalized = normalizeContentArray(input);
    return {
      data: { root: {}, content: normalized.content },
      unsupportedTypes: normalized.unsupportedTypes,
    };
  }

  if (input && typeof input === "object") {
    const candidate = input as { root?: unknown; content?: unknown; zones?: unknown };
    if (Array.isArray(candidate.content)) {
      const normalized = normalizeContentArray(candidate.content);
      return {
        data: {
          root: candidate.root && typeof candidate.root === "object"
            ? candidate.root as Record<string, unknown>
            : {},
          content: normalized.content,
        },
        unsupportedTypes: normalized.unsupportedTypes,
      };
    }
  }

  return {
    data: { root: {}, content: [] },
    unsupportedTypes: [],
  };
}

export const blogPuckConfig = {
  ...stylablePuckConfig,
  categories: {
    "Story": ["Heading", "Text", "RichText", "Blockquote", "CodeBlock", "Divider"],
    "Media": ["Image", "ImageEnhanced", "MapEmbed"],
    "Conversion": ["Button", "Hero"],
  },
  components: Object.fromEntries(
    BLOG_COMPONENT_NAMES.map((name) => [name, stylablePuckConfig.components[name as BlogComponentName]]),
  ),
};
